import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { MessageSquareIcon, XIcon, SendIcon, SparklesIcon } from 'lucide-react';
import { ASSISTANT_DISCLOSURE, ASSISTANT_FALLBACK, ASSISTANT_FALLBACK_LINKS, ASSISTANT_GREETING, assistantTopics, suggestedQuestions, type AssistantLink } from '../../data/assistantKnowledge';
import { useRegistry } from '../../contexts/RegistryContext';
interface Turn {
  id: string;
  role: 'user' | 'assistant';
  body: string;
  links?: AssistantLink[];
}
const greeting: Turn = {
  id: 'greeting',
  role: 'assistant',
  body: ASSISTANT_GREETING
};

/**
 * Transparent keyword scoring. Multi-word phrases weigh heaviest, single terms
 * match on word stems so "interviews" still finds "interview". Safety-critical
 * topics are checked first so they can never be outscored by a casual match.
 */
function answerFor(question: string): Turn {
  const text = ` ${question.toLowerCase().replace(/[^a-z0-9à-ÿ’' ]+/g, ' ').replace(/\s+/g, ' ')} `;
  const priority = new Set(['wellbeing-serious', 'legal-medical-financial', 'fraud']);
  let best: {
    score: number;
    topic: (typeof assistantTopics)[number];
  } | null = null;
  for (const topic of assistantTopics) {
    let score = 0;
    for (const keyword of topic.keywords) {
      const term = keyword.trim();
      if (!term) continue;
      const hit = term.includes(' ') ? text.includes(` ${term} `) || text.includes(term) : text.includes(` ${term}`);
      if (hit) score += term.length + (term.includes(' ') ? 6 : 0);
    }
    if (score === 0) continue;
    if (priority.has(topic.id)) score += 40;
    if (!best || score > best.score) best = {
      score,
      topic
    };
  }
  if (!best) {
    return {
      id: `a-${Date.now()}`,
      role: 'assistant',
      body: ASSISTANT_FALLBACK,
      links: ASSISTANT_FALLBACK_LINKS
    };
  }
  return {
    id: `a-${Date.now()}`,
    role: 'assistant',
    body: best.topic.answer,
    links: best.topic.links
  };
}
export function VelnorisAssistant() {
  const [open, setOpen] = useState(false);
  const [turns, setTurns] = useState<Turn[]>([greeting]);
  const [draft, setDraft] = useState('');
  const [thinking, setThinking] = useState(false);
  const {
    logActivity
  } = useRegistry();
  const panelRef = useRef<HTMLDivElement>(null);
  const logRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const launcherRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        launcherRef.current?.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);
  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);
  useEffect(() => {
    logRef.current?.scrollTo({
      top: logRef.current.scrollHeight,
      behavior: 'smooth'
    });
  }, [turns, thinking]);
  const ask = (question: string) => {
    const trimmed = question.trim();
    if (!trimmed || thinking) return;
    setTurns(prev => [...prev, {
      id: `u-${Date.now()}`,
      role: 'user',
      body: trimmed
    }]);
    setDraft('');
    setThinking(true);
    logActivity('assistant', 'Question asked of Velnoris AI', trimmed);
    window.setTimeout(() => {
      setTurns(prev => [...prev, answerFor(trimmed)]);
      setThinking(false);
    }, 420);
  };
  return <>
      <button ref={launcherRef} type="button" onClick={() => setOpen(v => !v)} aria-expanded={open} aria-controls="velnoris-ai-panel" className="fixed bottom-5 right-5 z-50 inline-flex items-center gap-2.5 rounded-sm border border-gold bg-ink px-4 py-3 font-sans text-[0.8125rem] text-parchment shadow-lg transition-[background-color,color,transform] duration-120 ease-institutional hover:bg-navy-800 active:translate-y-px md:bottom-8 md:right-8" data-id="element-428" data-mp="12wxgs5h58m2q">
        {open ? <XIcon aria-hidden="true" className="h-4 w-4 text-gold" data-id="element-429" data-mp="2j9unv2z3vz7f" /> : <MessageSquareIcon aria-hidden="true" className="h-4 w-4 text-gold" data-id="element-430" data-mp="3elm2h36p9oac" />}
        {open ? 'Close' : 'Velnoris AI'}
      </button>

      {open ? <div ref={panelRef} id="velnoris-ai-panel" role="dialog" aria-modal="false" aria-label="Velnoris AI assistant" className="fixed bottom-20 right-5 z-50 flex max-h-[min(34rem,calc(100vh-7rem))] w-[calc(100vw-2.5rem)] max-w-[24rem] animate-rise-in flex-col border border-navy-200 bg-white shadow-2xl md:bottom-24 md:right-8" data-id="element-431" data-mp="3nl8sytxw59om">
          <div className="on-dark shrink-0 bg-ink px-5 py-4" data-id="element-432" data-mp="a6cflhxqgyao">
            <div className="flex items-center gap-2" data-id="element-433" data-mp="a85rbbn7e8pj">
              <SparklesIcon aria-hidden="true" className="h-4 w-4 text-gold" data-id="element-434" data-mp="f5fgqralekdd" />
              <h2 className="font-serif text-[1rem] text-parchment" data-id="element-435" data-mp="k4igbw7szydy">Velnoris AI</h2>
            </div>
            <p className="mt-2 font-sans text-[0.75rem] leading-relaxed text-navy-200" data-id="element-436" data-mp="p2ttafteap0e">{ASSISTANT_DISCLOSURE}</p>
          </div>

          <div ref={logRef} role="log" aria-live="polite" className="flex-1 space-y-4 overflow-y-auto bg-parchment px-5 py-5" data-id="element-437" data-mp="29m5oaf3qd5kv">
            {turns.map(turn => turn.role === 'user' ? <p key={turn.id} className="ml-auto max-w-[85%] rounded-sm bg-ink px-3.5 py-2.5 font-sans text-[0.8125rem] leading-relaxed text-parchment" data-id="element-438" data-mp="1znzny1xko7i4">
                  {turn.body}
                </p> : <div key={turn.id} className="max-w-[92%] border-l-2 border-gold bg-white px-3.5 py-3" data-id="element-439" data-mp="2nciw8hgmgka7">
                  <p className="font-sans text-[0.8125rem] leading-relaxed text-charcoal-700" data-id="element-440" data-mp="2il0uvsaoshop">{turn.body}</p>
                  {turn.links?.length ? <ul className="mt-3 space-y-1.5" data-id="element-441" data-mp="2dl4zghg21ykr">
                      {turn.links.map(link => <li key={link.to + link.label} data-id="element-442" data-mp="36bn43mwfg80a">
                          <Link to={link.to} onClick={() => setOpen(false)} className="font-sans text-[0.8125rem] text-navy-700 underline underline-offset-2 transition-colors duration-120 hover:text-ink" data-id="element-443" data-mp="36a3m9rv4howe">
                            {link.label}
                          </Link>
                        </li>)}
                    </ul> : null}
                </div>)}
            {thinking ? <p className="font-sans text-[0.75rem] text-charcoal-300" data-id="element-444" data-mp="ppgozf21wfc4">Velnoris AI is looking that up…</p> : null}
          </div>

          {turns.length === 1 ? <div className="shrink-0 border-t border-navy-100 bg-white px-5 py-4" data-id="element-445" data-mp="goa4yw070me4">
              <p className="font-sans text-micro uppercase tracking-[0.14em] text-charcoal-300" data-id="element-446" data-mp="gqd3y5jbppuz">Common questions</p>
              <ul className="mt-3 flex flex-wrap gap-2" data-id="element-447" data-mp="2b2b1vbc6mzbk">
                {suggestedQuestions.map(q => <li key={q} data-id="element-448" data-mp="3vk2gqq0y9zhp">
                    <button type="button" onClick={() => ask(q)} className="rounded-sm border border-navy-100 px-2.5 py-1.5 text-left font-sans text-[0.75rem] text-charcoal-700 transition-colors duration-120 hover:border-navy-400 hover:bg-parchment" data-id="element-449" data-mp="3vldjqnmkn37r">
                      {q}
                    </button>
                  </li>)}
              </ul>
            </div> : null}

          <form onSubmit={e => {
        e.preventDefault();
        ask(draft);
      }} className="flex shrink-0 items-center gap-2 border-t border-navy-100 bg-white px-3 py-3" data-id="element-450" data-mp="d5kuaarwrzdb">
            <label htmlFor="velnoris-ai-input" className="sr-only" data-id="element-451" data-mp="n2pggalr7uu4">
              Ask Velnoris AI a question
            </label>
            <input ref={inputRef} id="velnoris-ai-input" type="text" value={draft} onChange={e => setDraft(e.target.value)} placeholder="Ask me anything…" autoComplete="off" className="min-w-0 flex-1 border border-navy-100 bg-white px-3 py-2 font-sans text-[0.8125rem] text-ink placeholder:text-charcoal-200 transition-colors duration-120 hover:border-navy-300 focus:border-navy-500" data-id="element-452" data-mp="2owez10mg5qxf" />
            <button type="submit" disabled={!draft.trim() || thinking} className="inline-flex items-center justify-center rounded-sm border border-gold bg-gold p-2.5 text-ink transition-[background-color,transform] duration-120 ease-institutional hover:bg-gold-400 active:translate-y-px disabled:pointer-events-none disabled:opacity-45" data-id="element-453" data-mp="1k4n98z1cllo9">
              <SendIcon aria-hidden="true" className="h-4 w-4" data-id="element-454" data-mp="35hztamzok4ae" />
              <span className="sr-only" data-id="element-455" data-mp="35jj9kdlc6fc6">Send</span>
            </button>
          </form>
        </div> : null}
    </>;
}